import {
  Group,
  Stack,
  Text,
  Button,
  Badge,
  Box,
  Alert,
  Checkbox,
  Progress,
  Table,
} from '@mantine/core';
import {
  Clock,
  AlertCircle,
  Users,
  LogIn,
  Check,
  Play,
  CheckCircle,
  Circle,
  HelpCircle,
  X,
  Mail,
} from 'react-feather';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import type {
  CoachPrepGroupContext,
  CoachPrepRosterParticipant,
  CoachSessionPrep,
  PrepChecklistState,
} from '../../../../services/api/coachSessionPrep';
import type {
  ComposeEnvelope,
  PreSessionGuidanceOutput,
} from '../../../../services/api/aiTypes';
import { ParticipantAvatar } from '../../../../components/common';
import { ContentCard } from '../../../../components/common/ContentCard';
import { getParticipantDisplayName } from '../../../../utils/participantDisplayName';
import { RSVP_STATUS_CONFIG } from '../../../../config/statusConfig';
import { coachActiveSessionApi } from '../../../../services/api/coachActiveSession';
import { AiDisclosureModal } from '../../../../components/session/AiDisclosureModal';
import { AIListeningCard } from './AIListeningCard';
import { SentimentGuidancePanel } from './SentimentGuidancePanel';

/**
 * MED-793 / AC4 lower section — Group tabs + per-group context.
 *
 * Each tab shows:
 *   • Status row: "Group N — Scheduled / Ready to start" + Start Session button (MED-792)
 *   • Optional pre-work warning banner (when pre-work/RSVPs are missing)
 *   • Group Roster card (count + pre-work progress + per-participant table)
 *   • "Before you start" checklist (4 fixed items in v1)
 *   • MED-876 AC3 — AI Listening card on the right rail (gated on aiFeaturesEnabled)
 *   • MED-876 AC5 — Sentiment & Guidance panel below the roster (gated on aiFeaturesEnabled)
 */

interface CoachPrepGroupSectionProps {
  groups: CoachPrepGroupContext[];
  prep: CoachSessionPrep;
  onPatchChecklist: (patch: PrepChecklistState) => Promise<void>;
  /** MED-792 AC6 — launching the active session requires the cohort id for navigation. */
  cohortId: string;
  /** MED-792 AC7 — when true, show the AI disclosure modal before launching (Decision B1). */
  aiFeaturesEnabled: boolean;
  /**
   * MED-1080 — the sequence number of the session being prepped (null when the
   * session isn't numbered). Used to omit the "Noted carryover from prior
   * session" checklist item on Session 1, which has no prior session.
   */
  sessionNumber: number | null;
  /**
   * MED-1181 / AC4 — true when the authored agenda has reflection content
   * configured (reflection prompts at or above the minimum). When false the
   * roster's "Pre-session reflection" column is hidden, since there is nothing
   * for participants to complete and "Not completed" for everyone is misleading.
   */
  hasReflectionContent: boolean;
  /**
   * MED-1100 — DEV preview only: pre-seeded sentiment/guidance envelope handed to
   * the Sentiment & Guidance panel so it renders without the AI backend.
   */
  previewSentimentEnvelope?: ComposeEnvelope<PreSessionGuidanceOutput>;
}

// MED-1095 — Group Readiness Header tokens, matched to the Figma (ready banner
// node 1991:3959; scheduled state derived per AC3).
const READY_BANNER_BG = '#EBF2FA';
const READY_ICON_BG = '#203358';
const READY_PILL_BG = '#3B82B0';
const SCHEDULED_BANNER_BG = '#F3F4F6';
const SCHEDULED_ICON_BG = '#E5E7EB';
const SCHEDULED_ICON_COLOR = '#6B7280';
const BANNER_TITLE_COLOR = '#1A1A2E';
const BANNER_SUB_COLOR = '#6B7280';
const START_BTN_BG = '#203358';
const PREWORK_ALERT_BG = '#FEF3E2';
const PREWORK_ALERT_TEXT = '#1A1A2E';
const PREWORK_ALERT_ICON = '#D97706';

// MED-1096 — Group Roster tokens.
const PREWORK_BOX_BG = '#F9FAFB';
const PREWORK_BOX_BORDER = '#E5E7EB';
const PREWORK_BAR_COLOR = '#203358';
const REFLECTION_DONE_COLOR = 'var(--mantine-color-green-7)';
const COL_HEADER_COLOR = '#9CA3AF';
// MED-1097 — navy checkbox in the "Before you start" checklist.
const CHECKBOX_COLOR = '#203358';

/** Leading icon for an RSVP pill (MED-1096), keyed by status. */
function rsvpIcon(status: string) {
  switch (status) {
    case 'accepted':
      return <Check size={12} />;
    case 'declined':
      return <X size={12} />;
    case 'tentative':
      return <HelpCircle size={12} />;
    default:
      return <Clock size={12} />;
  }
}

/**
 * MED-1095 AC3/AC5 — calendar-day distance to a target (today = 0, tomorrow = 1,
 * …), so the scheduled sub-line reads "Today at {time}" on the day-of and
 * "{date} · N days away" for a future day — never "0 days away" / a bogus
 * "1 day away" 14 minutes out (which a raw ceil(ms/day) produced).
 */
function calendarDayDiff(target: Date): number {
  const now = new Date();
  const a = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  ).getTime();
  const b = new Date(
    target.getFullYear(),
    target.getMonth(),
    target.getDate()
  ).getTime();
  return Math.round((b - a) / 86400000);
}

/** Render first names as "A", "A and B", or "A, B, and C" with the names bold. */
function renderNameList(names: string[]) {
  return names.map((name, i) => {
    const sep =
      i === 0
        ? ''
        : i === names.length - 1
          ? `${names.length > 2 ? ',' : ''} and `
          : ', ';
    return (
      <span key={`${name}-${i}`}>
        {sep}
        <Text span fw={700} inherit>
          {name}
        </Text>
      </span>
    );
  });
}

const AI_ACK_STORAGE_KEY = (sessionId: string) =>
  `medley.activeSession.aiAck.${sessionId}`;
function markAiAcknowledged(sessionId: string): void {
  try {
    window.localStorage.setItem(AI_ACK_STORAGE_KEY(sessionId), '1');
  } catch {
    // Storage unavailable — the active page will re-show the modal on entry, acceptable.
  }
}

const CHECKLIST_ITEMS: {
  key: keyof PrepChecklistState;
  label: string;
  badge?: 'flag' | 'count';
}[] = [
  {
    key: 'reviewed_group_sentiment',
    label: 'Reviewed group sentiment and guidance',
  },
  { key: 'checked_agenda', label: 'Checked session agenda and timing' },
  {
    key: 'noted_carryover',
    label: 'Noted carryover from prior session',
    badge: 'flag',
  },
  {
    key: 'confirmed_attendance',
    label: 'Confirmed attendance',
    badge: 'count',
  },
];

export function CoachPrepGroupSection({
  groups,
  prep,
  onPatchChecklist,
  cohortId,
  aiFeaturesEnabled,
  sessionNumber,
  previewSentimentEnvelope,
  hasReflectionContent,
}: CoachPrepGroupSectionProps) {
  const [activeGroupId, setActiveGroupId] = useState<string>(
    groups[0]?.group_id ?? ''
  );

  if (groups.length === 0) {
    return (
      <ContentCard variant="elevated" radius={16} padding="lg" fill={false}>
        <Text c="dimmed" fs="italic">
          You're not assigned to any groups for this session yet.
        </Text>
      </ContentCard>
    );
  }

  const activeGroup =
    groups.find(g => g.group_id === activeGroupId) ?? groups[0];

  return (
    <ContentCard
      variant="elevated"
      radius={16}
      padding={0}
      contentGap={0}
      fill={false}
      data-testid="coach-prep-group-section"
      header={
        /* Tab strip lives in the card header (MED-1095). No container padding —
           each tab carries its own horizontal padding so the active underline
           reaches the card edge. */
        <Group gap={0} pt={6}>
          {groups.map(g => (
            <GroupTab
              key={g.group_id}
              group={g}
              active={g.group_id === activeGroup.group_id}
              onClick={() => setActiveGroupId(g.group_id)}
            />
          ))}
        </Group>
      }
    >
      <Box style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }} />

      {/* MED-1095 — the banner + pre-work alert are full-width strips that butt
          against the tabs divider above and each other (no gap, no radius). The
          rest of the body is padded below. */}
      <GroupStatusRow
        group={activeGroup}
        cohortId={cohortId}
        aiFeaturesEnabled={aiFeaturesEnabled}
      />
      {(activeGroup.has_missing_rsvps || activeGroup.has_missing_prework) && (
        <Alert
          variant="light"
          color="yellow"
          radius={0}
          icon={<AlertCircle size={16} />}
          styles={{
            root: { backgroundColor: PREWORK_ALERT_BG, padding: '12px 20px' },
            icon: { color: PREWORK_ALERT_ICON },
          }}
        >
          <Text size="sm" c={PREWORK_ALERT_TEXT}>
            {activeGroup.has_missing_prework && (
              <>
                {activeGroup.prework_total_count -
                  activeGroup.prework_submitted_count}{' '}
                of {activeGroup.prework_total_count} participants haven't
                submitted pre-work.
                {(() => {
                  const names = (activeGroup.participants ?? [])
                    .filter(p => !p.reflection_completed)
                    // MED-1162 — reminder list uses preferred-or-first (AC1/AC2).
                    .map(p => getParticipantDisplayName(p.preferred_name, p.first_name));
                  return names.length > 0 ? (
                    <> Send a reminder to {renderNameList(names)}.</>
                  ) : null;
                })()}{' '}
              </>
            )}
            {activeGroup.has_missing_rsvps && (
              <>
                {activeGroup.participant_count -
                  activeGroup.rsvp_accepted_count}{' '}
                participant
                {activeGroup.participant_count -
                  activeGroup.rsvp_accepted_count ===
                1
                  ? ' has'
                  : 's have'}{' '}
                not RSVP'd.
              </>
            )}
          </Text>
        </Alert>
      )}

      <Box p="lg">
        <Stack gap="md">
          <Group align="flex-start" gap="md" wrap="wrap">
            <Box style={{ flex: '1 1 480px' }}>
              <GroupRosterCard
                group={activeGroup}
                showReflectionColumn={hasReflectionContent}
              />
            </Box>
            <Box style={{ flex: '0 0 280px' }}>
              <Stack gap="md">
                <BeforeYouStartCard
                  group={activeGroup}
                  checklistState={prep.checklist_state}
                  onPatch={onPatchChecklist}
                  sessionNumber={sessionNumber}
                  aiFeaturesEnabled={aiFeaturesEnabled}
                />
                {/* MED-876 AC3 — AI Listening card per group. IV1: hidden when modality has AI disabled. */}
                {aiFeaturesEnabled && (
                  <AIListeningCard
                    sessionId={activeGroup.session_id}
                    enabled={activeGroup.ai_listening_enabled}
                    locked={activeGroup.ai_listening_locked}
                  />
                )}
              </Stack>
            </Box>
          </Group>
          {/* MED-876 AC5 — Sentiment & Guidance panel per group tab. Async load (IV5). */}
          {aiFeaturesEnabled && (
            <SentimentGuidancePanel
              sessionId={activeGroup.session_id}
              groupId={activeGroup.group_id}
              groupName={activeGroup.group_name}
              previewEnvelope={previewSentimentEnvelope}
            />
          )}
        </Stack>
      </Box>
    </ContentCard>
  );
}

// ---------------------------------------------------------------------------
// Tab strip item
// ---------------------------------------------------------------------------

function GroupTab({
  group,
  active,
  onClick,
}: {
  group: CoachPrepGroupContext;
  active: boolean;
  onClick: () => void;
}) {
  const start = group.scheduled_start ? new Date(group.scheduled_start) : null;
  const timeLabel = start
    ? `${start.toLocaleDateString(undefined, { weekday: 'short' })} ${start.toLocaleTimeString(
        undefined,
        { hour: 'numeric', minute: '2-digit' }
      )}`
    : '—';
  return (
    <Box
      component="button"
      onClick={onClick}
      style={{
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: '12px 20px',
        borderBottom: active
          ? '2px solid var(--mantine-color-dark-7)'
          : '2px solid transparent',
        color: active
          ? 'var(--mantine-color-dark-7)'
          : 'var(--mantine-color-gray-7)',
      }}
    >
      <Group gap={6} align="center">
        <Box
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            // Readiness dot: green when ready/live, amber when scheduled.
            background:
              group.is_live || group.status_label === 'ready_to_start'
                ? 'var(--mantine-color-medleyGreen-6)'
                : 'var(--mantine-color-yellow-6)',
          }}
        />
        <Text fz={14} fw={active ? 600 : 500}>
          {group.group_name}
        </Text>
        <Text fz={13} c="dimmed">
          {timeLabel} · {group.participant_count} participants
        </Text>
      </Group>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Status row (label + Start Session button — render only)
// ---------------------------------------------------------------------------

function GroupStatusRow({
  group,
  cohortId,
  aiFeaturesEnabled,
}: {
  group: CoachPrepGroupContext;
  cohortId: string;
  aiFeaturesEnabled: boolean;
}) {
  const navigate = useNavigate();
  const start = group.scheduled_start ? new Date(group.scheduled_start) : null;
  const isLive = group.is_live;
  const isReady = !isLive && group.status_label === 'ready_to_start';

  // MED-952 AC5 — a live group surfaces "Rejoin", not "Start".
  const statusText = isLive
    ? 'Live now'
    : isReady
      ? 'Ready to start'
      : 'Scheduled';

  const timeStr = start
    ? start.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;
  // MED-1095 AC3/AC5 — "Today at {time}" on the day-of, "{date} · N days away"
  // for a future day (never a bogus "0/1 day away" minutes out).
  const subline = (() => {
    if (!start) return null;
    const diff = calendarDayDiff(start);
    if (diff <= 0) return `Today at ${timeStr}`;
    const dateStr = start.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    return `${dateStr} · ${diff} ${diff === 1 ? 'day' : 'days'} away`;
  })();
  // MED-1095 AC2/AC5 — the ready state (always within the 15-min window) shows
  // minutes-to-start, not a days-away line.
  const minutesAway = start
    ? Math.round((start.getTime() - Date.now()) / 60000)
    : null;
  const startsInText =
    isReady && minutesAway !== null
      ? minutesAway <= 0
        ? 'Starting now'
        : `Starts in ${minutesAway} min`
      : null;

  const visual = isLive
    ? {
        bg: 'var(--mantine-color-medleyGreen-0)',
        iconBg: 'var(--mantine-color-medleyGreen-6)',
        iconColor: '#FFFFFF',
        icon: <Play size={20} fill="currentColor" />,
      }
    : isReady
      ? {
          bg: READY_BANNER_BG,
          iconBg: READY_ICON_BG,
          iconColor: '#FFFFFF',
          icon: <Play size={20} fill="currentColor" />,
        }
      : {
          bg: SCHEDULED_BANNER_BG,
          iconBg: SCHEDULED_ICON_BG,
          iconColor: SCHEDULED_ICON_COLOR,
          icon: <Clock size={20} />,
        };

  const [launching, setLaunching] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);

  // MED-792 AC6 — launch the active session.
  const performLaunch = async () => {
    setLaunching(true);
    try {
      await coachActiveSessionApi.startSession(group.session_id);
      // Acknowledge the AI disclosure so the active page doesn't re-show it.
      // Only pre-ack when listening is actually on for this session — otherwise
      // there is nothing to acknowledge (MED-876 Post-QA Patch 4).
      if (aiFeaturesEnabled && group.ai_listening_enabled) {
        markAiAcknowledged(group.session_id);
      }
      navigate(
        `/coach/cohorts/${cohortId}/sessions/${group.session_id}/active`
      );
    } catch (err) {
      notifications.show({
        color: 'red',
        title: "Couldn't start session",
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setLaunching(false);
    }
  };

  const handleStartClick = () => {
    // AC7 — disclosure modal fires only when AI listening is actually on for
    // this session. The modal copy describes recording behavior; showing it
    // when listening is locked off is materially false (MED-876 Post-QA
    // Patch 4 — matches the gates in CoachActiveSessionPage and waiting room).
    if (aiFeaturesEnabled && group.ai_listening_enabled) {
      setAiModalOpen(true);
      return;
    }
    void performLaunch();
  };

  // MED-952 AC5/AC6 — rejoin a session that is already in_progress. No /start
  // call needed (it is idempotent on in_progress anyway): navigating to the
  // active screen re-fetches the same session state and restores the coach to
  // the same section index. The active page owns the AI disclosure gate on
  // mount, so we route straight there.
  const handleRejoinClick = () => {
    navigate(`/coach/cohorts/${cohortId}/sessions/${group.session_id}/active`);
  };

  return (
    <Group
      justify="space-between"
      align="center"
      py="md"
      px="lg"
      wrap="nowrap"
      style={{ background: visual.bg }}
    >
      <Group gap="md" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
        <Box
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: visual.iconBg,
            color: visual.iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {visual.icon}
        </Box>
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text fz={15} fw={600} c={BANNER_TITLE_COLOR}>
            {group.group_name} — {statusText}
          </Text>
          {subline && (
            <Text fz={13} c={BANNER_SUB_COLOR}>
              {subline}
            </Text>
          )}
        </Stack>
        {startsInText && (
          <Box
            style={{
              background: READY_PILL_BG,
              borderRadius: 20,
              padding: '3px 12px',
              flexShrink: 0,
            }}
          >
            <Text
              fz={13}
              fw={600}
              c="#FFFFFF"
              style={{ whiteSpace: 'nowrap', transform: 'translateY(1px)' }}
            >
              {startsInText}
            </Text>
          </Box>
        )}
      </Group>
      {isLive ? (
        <Button
          variant="filled"
          color="medleyGreen"
          size="sm"
          radius={10}
          leftSection={<LogIn size={16} />}
          onClick={handleRejoinClick}
          data-testid="coach-prep-rejoin-session-button"
          style={{ flexShrink: 0 }}
        >
          Rejoin Session — {group.group_name}
        </Button>
      ) : (
        <Button
          variant="filled"
          size="sm"
          radius={10}
          disabled={!isReady}
          onClick={handleStartClick}
          loading={launching}
          styles={
            isReady ? { root: { backgroundColor: START_BTN_BG } } : undefined
          }
          data-testid="coach-prep-start-session-button"
          style={{ flexShrink: 0 }}
        >
          Start Session — {group.group_name}
        </Button>
      )}

      <AiDisclosureModal
        opened={aiModalOpen}
        role="coach"
        onAcknowledge={() => {
          setAiModalOpen(false);
          void performLaunch();
        }}
        onDismiss={() => setAiModalOpen(false)}
      />
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Group Roster card
// ---------------------------------------------------------------------------

function GroupRosterCard({
  group,
  showReflectionColumn,
}: {
  group: CoachPrepGroupContext;
  /** MED-1181 / AC4 — hide the "Pre-session reflection" column when no reflection content is configured. */
  showReflectionColumn: boolean;
}) {
  const total = group.prework_total_count;
  const submitted = group.prework_submitted_count;
  const missing = Math.max(0, total - submitted);
  const pct = total > 0 ? Math.round((submitted / total) * 100) : 0;
  const participants = group.participants ?? [];

  const colHeader = (label: string) => (
    <Text
      fz={11}
      fw={600}
      c={COL_HEADER_COLOR}
      tt="uppercase"
      style={{ letterSpacing: 0.5 }}
    >
      {label}
    </Text>
  );

  return (
    <ContentCard
      variant="bordered"
      radius={16}
      padding="lg"
      contentGap="md"
      fill={false}
      header={
        <Group justify="space-between" align="center">
          <Group gap={8} align="center">
            <Users size={22} color={BANNER_SUB_COLOR} aria-hidden="true" />
            <Text fz={18} fw={700} c={BANNER_TITLE_COLOR}>
              {group.group_name} Roster
            </Text>
          </Group>
          {/* AC3 — attending count is plain gray text, not a badge. */}
          <Text fz={13} c="dimmed">
            {group.rsvp_accepted_count}/{group.participant_count} attending
          </Text>
        </Group>
      }
    >
      {/* AC4 — pre-work progress in a contained rounded box, thicker bar, amber
          "N missing" pill. */}
      <Box
        mb={0}
        style={{
          border: `1px solid ${PREWORK_BOX_BORDER}`,
          borderRadius: 10,
          background: PREWORK_BOX_BG,
          padding: '12px 16px',
        }}
      >
        <Group gap="md" align="center" wrap="nowrap">
          <Text size="sm" style={{ whiteSpace: 'nowrap' }}>
            Pre-work:{' '}
            <Text span fw={700} inherit>
              {submitted} of {total} submitted
            </Text>
          </Text>
          <Progress
            value={pct}
            color={PREWORK_BAR_COLOR}
            style={{ flex: 1 }}
            radius="xl"
            size="md"
          />
          {missing > 0 && (
            <Badge
              variant="light"
              radius="xl"
              styles={{
                root: {
                  backgroundColor: PREWORK_ALERT_BG,
                  color: PREWORK_ALERT_ICON,
                },
                label: {
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                  transform: 'translateY(1px)',
                },
              }}
            >
              {missing} missing
            </Badge>
          )}
        </Group>
      </Box>

      {/* MED-1044 — per-participant roster: name / pre-session reflection / RSVP.
          Renders as soon as participants are assigned, independent of whether any
          reflection or RSVP has come in (AC1). The server pre-sorts rows by
          reflection status then first name (AC5). */}
      {participants.length === 0 ? (
        <Text size="xs" c="dimmed" fs="italic">
          No participants are assigned to this group yet.
        </Text>
      ) : (
        <Table
          verticalSpacing="sm"
          horizontalSpacing={0}
          styles={{
            thead: { backgroundColor: 'transparent' },
            th: { backgroundColor: 'transparent', paddingTop: 6 },
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{colHeader('Participant')}</Table.Th>
              {/* MED-1181 / AC4 — reflection column only when content is configured. */}
              {showReflectionColumn && (
                <Table.Th>{colHeader('Pre-session reflection')}</Table.Th>
              )}
              {/* Header left-aligned; the badges below stay flush right. */}
              <Table.Th>{colHeader('RSVP')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {participants.map((p, i) => (
              <RosterParticipantRow
                key={p.user_id}
                participant={p}
                isLast={i === participants.length - 1}
                showReflectionColumn={showReflectionColumn}
              />
            ))}
          </Table.Tbody>
        </Table>
      )}
    </ContentCard>
  );
}

/** MED-1044 — one roster row: name (AC2), reflection status (AC3), RSVP (AC4). */
function RosterParticipantRow({
  participant,
  isLast,
  showReflectionColumn,
}: {
  participant: CoachPrepRosterParticipant;
  isLast: boolean;
  /** MED-1181 / AC4 — render the reflection status cell only when the column is shown. */
  showReflectionColumn: boolean;
}) {
  const rsvpStatus = participant.rsvp_status ?? 'pending';
  const rsvpCfg = RSVP_STATUS_CONFIG[rsvpStatus] ?? RSVP_STATUS_CONFIG.pending;
  // Match the design: preferred-or-first name + last initial (e.g. "Jordan K.").
  // MED-1162 — label by preferred name when set (AC1/AC2).
  const displayFirstName = getParticipantDisplayName(
    participant.preferred_name,
    participant.first_name,
  );
  const displayName = participant.last_name
    ? `${displayFirstName} ${participant.last_name.charAt(0)}.`
    : displayFirstName;
  // Last row drops its bottom cell padding so the card's bottom padding reads
  // evenly (no doubled-up space under the final row).
  const tdStyle = isLast ? { paddingBottom: 0 } : undefined;
  return (
    <Table.Tr>
      <Table.Td style={tdStyle}>
        <Group gap="sm" wrap="nowrap">
          <ParticipantAvatar
            // MED-1162 (AC3) — initials follow the displayed (preferred-or-first)
            // name; colorKey pinned to the formal name so the color is stable.
            firstName={displayFirstName}
            lastName={participant.last_name}
            colorKey={`${participant.first_name}${participant.last_name}`}
            size={32}
            initialsOffsetY={2}
          />
          <Text size="sm" fw={500}>
            {displayName}
          </Text>
        </Group>
      </Table.Td>
      {/* MED-1181 / AC4 — reflection status cell only when the column is shown. */}
      {showReflectionColumn && (
        <Table.Td style={tdStyle}>
          {/* AC5 — icon + sentence-case text (no badge, no "View" link). */}
          {participant.reflection_completed ? (
            <Group gap={6} align="center" wrap="nowrap">
              <CheckCircle size={15} color={REFLECTION_DONE_COLOR} />
              <Text size="sm" fw={500} c={REFLECTION_DONE_COLOR}>
                Completed
              </Text>
            </Group>
          ) : (
            <Group gap={6} align="center" wrap="nowrap">
              <Circle size={15} color={PREWORK_ALERT_ICON} />
              <Text size="sm" fw={500} c={PREWORK_ALERT_ICON}>
                Not completed
              </Text>
            </Group>
          )}
        </Table.Td>
      )}
      <Table.Td style={tdStyle}>
        {/* AC6 — config-driven color pill, sentence case, with a status icon.
            MED-1197 (AC3) — pre-invite (invite_sent=false) there is no RSVP to
            await yet, so render a DISTINCT "Invitation pending" pill (blue, mail
            icon). It must read as neutral/informational — never like the red
            "Declined" or the gray "Awaiting" (invited, no reply) pill — so the
            coach can tell "not yet invited" from "invited, no response." */}
        <Group justify="flex-start">
          {participant.invite_sent ? (
            <Badge
              variant="light"
              color={rsvpCfg.color}
              radius="xl"
              leftSection={rsvpIcon(rsvpStatus)}
              styles={{
                label: {
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                  transform: 'translateY(1px)',
                },
              }}
            >
              {rsvpCfg.label}
            </Badge>
          ) : (
            <Badge
              variant="light"
              color="blue"
              radius="xl"
              leftSection={<Mail size={12} />}
              styles={{
                label: {
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                  transform: 'translateY(1px)',
                },
              }}
            >
              Invitation pending
            </Badge>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

// ---------------------------------------------------------------------------
// "Before you start" checklist card
// ---------------------------------------------------------------------------

function BeforeYouStartCard({
  group,
  checklistState,
  onPatch,
  sessionNumber,
  aiFeaturesEnabled,
}: {
  group: CoachPrepGroupContext;
  checklistState: PrepChecklistState;
  onPatch: (patch: PrepChecklistState) => Promise<void>;
  sessionNumber: number | null;
  /** MED-1181 AC5 — gates the AI-derived "Reviewed group sentiment and guidance" row. */
  aiFeaturesEnabled: boolean;
}) {
  const handleToggle = (key: keyof PrepChecklistState) => {
    const next = !checklistState[key];
    void onPatch({ [key]: next });
  };

  // MED-1080 — the carryover item only applies when a prior session exists.
  // Session 1 (session_number === 1) has none, so omit it there; show it on
  // every other session (including non-numbered sessions, where session_number
  // is null and we can't assert it's the first in the sequence).
  // MED-1181 AC5 — the "Reviewed group sentiment and guidance" item is an AI
  // affordance (the sentiment panel it refers to is itself gated on
  // aiFeaturesEnabled); omit the checklist row when the cohort's modality AI is
  // off so the coach isn't asked to review guidance that never renders.
  const visibleItems = CHECKLIST_ITEMS.filter(
    item =>
      (item.key !== 'noted_carryover' || sessionNumber !== 1) &&
      (item.key !== 'reviewed_group_sentiment' || aiFeaturesEnabled)
  );

  return (
    <ContentCard
      variant="bordered"
      radius={16}
      padding="lg"
      contentGap="xs"
      fill={false}
      header={
        <Text fz={16} fw={700} c={BANNER_TITLE_COLOR}>
          Before you start · {group.group_name}
        </Text>
      }
    >
      <Stack gap={0}>
        {visibleItems.map((item, i) => (
          <Box
            key={item.key}
            py="sm"
            style={{
              borderBottom:
                i < visibleItems.length - 1
                  ? `1px solid ${PREWORK_BOX_BORDER}`
                  : undefined,
            }}
          >
            <Group
              gap="sm"
              align="center"
              justify="space-between"
              wrap="nowrap"
            >
              <Checkbox
                color={CHECKBOX_COLOR}
                checked={Boolean(checklistState[item.key])}
                onChange={() => handleToggle(item.key)}
                label={
                  <Text
                    size="sm"
                    style={{
                      textDecoration: checklistState[item.key]
                        ? 'line-through'
                        : 'none',
                      color: checklistState[item.key]
                        ? 'var(--mantine-color-gray-5)'
                        : undefined,
                    }}
                  >
                    {item.label}
                  </Text>
                }
              />
              {/* MED-1100 — soft-chip treatment shared with the Sentiment &
                  Guidance source tags (4px radius, soft tint + darker text,
                  12px bold). */}
              {item.badge === 'flag' && (
                <Box
                  style={{
                    backgroundColor: '#fee4e2',
                    borderRadius: 4,
                    padding: '2px 8px',
                    flexShrink: 0,
                  }}
                >
                  <Text
                    fz={12}
                    fw={700}
                    c="#b42318"
                    style={{ lineHeight: 1.5 }}
                  >
                    Flag
                  </Text>
                </Box>
              )}
              {item.badge === 'count' && group.rsvp_accepted_count > 0 && (
                <Box
                  style={{
                    backgroundColor: '#dbeafe',
                    borderRadius: 4,
                    padding: '2px 8px',
                    flexShrink: 0,
                  }}
                >
                  <Text
                    fz={12}
                    fw={700}
                    c="#1d4ed8"
                    style={{ lineHeight: 1.5, whiteSpace: 'nowrap' }}
                  >
                    {group.rsvp_accepted_count} confirmed
                  </Text>
                </Box>
              )}
            </Group>
          </Box>
        ))}
      </Stack>
    </ContentCard>
  );
}
